import Lightbox from '@/components/Lightbox';
import TooltipComponent from '@/components/Tooltip';
import {
  InfoTooltip,
  TooltipContainer,
} from '@/components_v2/Questions/type/common';
import { withUploadProvider } from '@/hooks/upload/uploadProvider';
import { attachmentsStorage } from '@/modules/shared/adapters/CloudStorage';
import Field from '@/modules/WebForm/context/Field';
import { linkify } from '@/utils/linkify';
import { Flex, HStack, IconButton, Link, Tag, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { FiImage } from 'react-icons/fi';

import { AnswerHistoryContext } from '../../context/AnswerHistoryContext';
import useFormDetails from '../../hooks/useFormDetails';
import { IQuestionState } from '../../types';
import QuestionContent from '../QuestionContent';
import { QuestionItemProvider, useQuestionItem } from './context';
import { QuestionAppend } from './QuestionAppend';

import { useTranslation } from 'react-i18next';

type QuestionItemProps = {
  question: IQuestionState;
};

const Content = () => {
  const { question } = useQuestionItem();
  const { formulary } = useFormDetails();
  const { t } = useTranslation();

  const { name, description, required, imageUrl } = question;

  const linkedDescription = description ? linkify(description) : null;

  return (
    <Flex
      w="full"
      marginTop="4"
      overflow="visible"
      boxShadow="initial"
      flexDir="column"
      borderWidth="thin"
      rounded="xl"
      bg="white"
      role="group"
      display={question.questionType === 'score' ? 'none' : 'flex'}
    >
      <VStack spacing="1" align="flex-start" w="full">
        <Flex justify="space-between" align="flex-start" w="full" padding="4">
          <VStack w="full" pr="4" align="flex-start">
            <HStack alignItems="center" spacing="2.5">
              <Text
                color="darkText"
                fontWeight="bold"
                fontSize="15px"
                lineHeight={1}
                translate="no"
              >
                {name}
              </Text>
              {question.tipText && (
                <TooltipComponent
                  description={question.tipText}
                  placement="top right"
                >
                  <TooltipContainer>
                    <InfoTooltip />
                  </TooltipContainer>
                </TooltipComponent>
              )}
            </HStack>
            <VStack spacing="3" align="flex-start" w="full">
              {linkedDescription && (
                <Text
                  maxW="90%"
                  color="gray.400"
                  fontWeight="bold"
                  fontSize="sm"
                  display="flex"
                  translate="no"
                >
                  {linkedDescription.map((part) =>
                    part.type === 'link' ? (
                      <Link key={part.key} href={part.value} isExternal mx={1}>
                        {part.value}
                      </Link>
                    ) : (
                      <Text key={part.key} translate="no">{part.value}</Text>
                    ),
                  )}
                </Text>
              )}
              {required && (
                <Tag size="md" colorScheme="orange">
                  {t('enum.required')}
                </Tag>
              )}

              <Flex w="full">
                <AnswerHistoryContext.Provider
                  value={{ disabled: !!formulary?.disableAnswerHistory }}
                >
                  <QuestionContent {...question} />
                </AnswerHistoryContext.Provider>
              </Flex>
            </VStack>
          </VStack>
          {imageUrl && (
            <IconButton
              aria-label="Ver imagem instrutiva"
              icon={<FiImage size={20} />}
              variant="ghost"
              size="md"
              onClick={() => Lightbox.openLightbox([imageUrl], 0)}
            />
          )}
        </Flex>
        <QuestionAppend disableAttachements={formulary?.disableAttachments} />
      </VStack>
    </Flex>
  );
};

const Wrapper: React.FC<QuestionItemProps> = ({ question }) => {
  return (
    <Field fieldId={question.id}>
      <QuestionItemProvider question={question}>
        <Content />
      </QuestionItemProvider>
    </Field>
  );
};

export const QuestionItem = withUploadProvider(attachmentsStorage)(Wrapper);
